import { HTTPBaseService } from "./HTTPBaseService";
import type { ServiceResponse } from "@/types/RequestResponseModels";
import type TransactionReportDetailDto from "@/types/Payment/TransactionReportDetailDto";

export interface ModuleAccess { Payment: boolean; Gateway: boolean }
export type GatewayPaymentRequestStatus =
  | "Created" | "CheckoutOpened" | "Processing" | "Paid" | "Failed" | "Expired"
  | "Cancelled" | "PartiallyRefunded" | "Refunded" | "RequiresReview";
/** Talep oluşturulduğu anda sabitlenen kart alım yöntemi; ödeme yalnız bu kanaldan yapılabilir. */
export type GatewayCardCollectionMode = "HostedCheckout" | "DirectApi";
export interface GatewayPaymentRequest {
  id: string; partnerOrderId: string; amount: number; currency: string;
  status: GatewayPaymentRequestStatus; source: string; module: string; cardCollectionMode: GatewayCardCollectionMode;
  createdAtUtc: string; expiresAtUtc: string; paidAtUtc?: string | null; refundedAmount: number; lastErrorCode?: string | null;
}
export interface GatewayPaymentRequestPage { items: GatewayPaymentRequest[]; total: number; page: number; pageSize: number }

/** Talebe bağlı kart denemesi (Transactions). Kart numarası işlem kaydındaki maskeli biçimdir. */
export interface GatewayPaymentAttempt {
  appTransactionId: string; startDate: string; transactionStatus: number; amount: number; installmentCount: number;
  cardName?: string | null; cardNumber?: string | null; providerReturnCode?: string | null; providerReturnDesc?: string | null;
  threeDAuthenticationResult?: number | null; isPaidAttempt: boolean;
}
/** Partnere gönderilen sonuç bildirimi (webhook) teslim kaydı. */
export interface GatewayNotification {
  eventId: string; eventType: string; status: "Pending" | "Delivered" | "Failed"; attemptCount: number;
  createdAtUtc: string; lastAttemptAtUtc?: string | null; nextAttemptAtUtc?: string | null; deliveredAtUtc?: string | null;
  lastHttpStatus?: number | null; lastError?: string | null;
}
export interface GatewayPaymentRequestDetail extends Omit<GatewayPaymentRequest, "source" | "module"> {
  source: string; module: string; maxInstallmentCount?: number | null; description?: string | null;
  buyerName?: string | null; buyerEmail?: string | null; buyerPhone?: string | null;
  returnUrl: string; callbackUrl?: string | null; closedAtUtc?: string | null; lastErrorMessage?: string | null;
  attempts: GatewayPaymentAttempt[]; notifications: GatewayNotification[];
}

export class GatewayService extends HTTPBaseService {
  constructor() { super(import.meta.env.VITE_API_BASE); }
  async capabilities(): Promise<ModuleAccess> {
    return (await this.instance.get<ModuleAccess>("/gateway/capabilities")).data;
  }
  async paymentRequests(params: {
    page: number; pageSize: number; search?: string; from?: string; to?: string; status?: GatewayPaymentRequestStatus;
    cardCollectionMode?: GatewayCardCollectionMode
  }): Promise<GatewayPaymentRequestPage> {
    return (await this.instance.get<GatewayPaymentRequestPage>("/gateway/payment-requests", { params })).data;
  }
  async paymentRequestDetail(id: string): Promise<GatewayPaymentRequestDetail> {
    return (await this.instance.get<GatewayPaymentRequestDetail>(`/gateway/payment-requests/${encodeURIComponent(id)}`)).data;
  }
  /** Kart denemesinin ödeme sonucu detayı (işlem raporu detayıyla aynı veri). */
  async attemptDetail(id: string, appTransactionId: string): Promise<ServiceResponse<TransactionReportDetailDto>> {
    return (await this.instance.get<ServiceResponse<TransactionReportDetailDto>>(
      `/gateway/payment-requests/${encodeURIComponent(id)}/attempts/${encodeURIComponent(appTransactionId)}`)).data;
  }
}
